import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { jwtDecode } from "jwt-decode";
import { useAuth } from "./AuthContext";
import { useOrgFilter } from "./OrgFilterContext";
import { useDmSocket } from "./DmSocketContext";
import { useDmDrawer } from "./DmDrawerContext";
import { sendDmMessage, toggleDmReaction, DM_QUICK_REACT_EMOJIS } from "@/lib/dmApi";
import { playDmSound } from "@/lib/dmSound";
import {
  getDesktopNotifPref,
  setDesktopNotifPref,
  isDesktopNotificationsEnabled,
} from "@/lib/presence";
import { isConversationMuted } from "@/lib/dmMuteCache";
import {
  isDmViewedElsewhere,
  isDmViewingHere,
} from "@/lib/dmViewingSync";
import { Button } from "@/components/ui/button";
import { X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { fetchUserMe } from "@/lib/userMeCache";
import { plainExcerpt } from "@/lib/markdownPlain";

const DmAlertContext = createContext(null);

function isViewingConversation(pathname, orgId, conversationId) {
  if (!orgId || !conversationId) return false;
  const enc = `/o/${encodeURIComponent(orgId)}/messages/${conversationId}`;
  const raw = `/o/${orgId}/messages/${conversationId}`;
  return pathname === enc || pathname === raw;
}

export function DmAlertProvider({ children }) {
  const { accessToken } = useAuth();
  const { selectedOrgId } = useOrgFilter();
  const { subscribe, orgId: socketOrgId } = useDmSocket();
  const { open, minimized, activeConversationId, openConversation } = useDmDrawer();
  const location = useLocation();
  const [toasts, setToasts] = useState([]);
  const [permPrompt, setPermPrompt] = useState(false);
  const [myPresence, setMyPresence] = useState("active");
  const [desktopEnabled, setDesktopEnabledState] = useState(() =>
    isDesktopNotificationsEnabled()
  );
  const seenPromptRef = useRef(false);
  const promptSourceRef = useRef(null); // "messages" | "background"
  const toastTimersRef = useRef(new Map());
  const heldToastsRef = useRef(new Set());

  const MESSAGES_TIP_SESSION_KEY = "dm-desktop-notif-messages-tip-dismissed";

  const wasMessagesTipDismissed = () => {
    try {
      return sessionStorage.getItem(MESSAGES_TIP_SESSION_KEY) === "1";
    } catch {
      return false;
    }
  };

  const markMessagesTipDismissed = () => {
    try {
      sessionStorage.setItem(MESSAGES_TIP_SESSION_KEY, "1");
    } catch {
      /* ignore */
    }
  };

  const clearToastTimer = useCallback((id) => {
    const timer = toastTimersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      toastTimersRef.current.delete(id);
    }
  }, []);

  const scheduleToastDismiss = useCallback(
    (id, ms = 12_000) => {
      clearToastTimer(id);
      if (heldToastsRef.current.has(id)) return;
      const timer = setTimeout(() => {
        toastTimersRef.current.delete(id);
        heldToastsRef.current.delete(id);
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, ms);
      toastTimersRef.current.set(id, timer);
    },
    [clearToastTimer]
  );

  const currentUserId = (() => {
    try {
      return accessToken ? jwtDecode(accessToken)?.sub : null;
    } catch {
      return null;
    }
  })();

  useEffect(() => {
    if (!accessToken) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchUserMe(accessToken);
        if (!cancelled) {
          setMyPresence(data?.presence_status || "active");
        }
      } catch {
        /* ignore */
      }
    })();
    const onPresence = (e) => {
      if (e?.detail) setMyPresence(e.detail);
    };
    window.addEventListener("presence-changed", onPresence);
    return () => {
      cancelled = true;
      window.removeEventListener("presence-changed", onPresence);
    };
  }, [accessToken]);

  const dismiss = useCallback(
    (id) => {
      clearToastTimer(id);
      heldToastsRef.current.delete(id);
      setToasts((prev) => prev.filter((t) => t.id !== id));
    },
    [clearToastTimer]
  );

  const holdToast = useCallback(
    (id) => {
      heldToastsRef.current.add(id);
      clearToastTimer(id);
    },
    [clearToastTimer]
  );

  const releaseToast = useCallback(
    (id) => {
      heldToastsRef.current.delete(id);
      // Shorter window after leaving reply so it doesn't linger forever
      scheduleToastDismiss(id, 8_000);
    },
    [scheduleToastDismiss]
  );

  const dismissForConversation = useCallback(
    (conversationId) => {
      if (conversationId == null) return;
      setToasts((prev) => {
        const next = [];
        for (const t of prev) {
          if (Number(t.conversationId) === Number(conversationId)) {
            clearToastTimer(t.id);
            heldToastsRef.current.delete(t.id);
          } else {
            next.push(t);
          }
        }
        return next;
      });
    },
    [clearToastTimer]
  );

  const pushToast = useCallback(
    (toast) => {
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
      setToasts((prev) => {
        const next = [{ ...toast, id }, ...prev].slice(0, 4);
        // Drop timers for toasts that fell off the stack
        const keep = new Set(next.map((t) => t.id));
        for (const [tid] of toastTimersRef.current) {
          if (!keep.has(tid)) {
            clearToastTimer(tid);
            heldToastsRef.current.delete(tid);
          }
        }
        return next;
      });
      scheduleToastDismiss(id, 12_000);
    },
    [scheduleToastDismiss, clearToastTimer]
  );

  const requestDesktopPermission = useCallback(async () => {
    if (typeof Notification === "undefined") {
      setDesktopNotifPref("denied");
      setDesktopEnabledState(false);
      return "denied";
    }
    try {
      // Already granted — treat as opt-in without re-prompting
      if (Notification.permission === "granted") {
        setDesktopNotifPref("granted");
        setDesktopEnabledState(true);
        return "granted";
      }
      if (Notification.permission === "denied") {
        setDesktopNotifPref("denied");
        setDesktopEnabledState(false);
        return "denied";
      }
      const result = await Notification.requestPermission();
      const ok = result === "granted";
      setDesktopNotifPref(ok ? "granted" : "denied");
      setDesktopEnabledState(ok);
      return result;
    } catch {
      setDesktopNotifPref("denied");
      setDesktopEnabledState(false);
      return "denied";
    }
  }, []);

  const setDesktopEnabled = useCallback(
    async (enabled) => {
      if (enabled) {
        const p = await requestDesktopPermission();
        const ok = p === "granted";
        setDesktopEnabledState(ok);
        return ok;
      }
      setDesktopNotifPref("denied");
      setDesktopEnabledState(false);
      return false;
    },
    [requestDesktopPermission]
  );

  /** @returns {boolean} true if a desktop notification was shown */
  const maybeDesktopNotify = useCallback(
    ({ title, body, conversationId, orgId }) => {
      if (typeof Notification === "undefined") return false;
      if (!document.hidden) return false;
      const pref = getDesktopNotifPref();
      if (pref === "denied") return false;
      if (Notification.permission === "granted" && pref !== "denied") {
        const n = new Notification(title, {
          body,
          tag: `dm-${orgId}-${conversationId}`,
        });
        n.onclick = () => {
          window.focus();
          dismissForConversation(conversationId);
          openConversation?.(conversationId);
          n.close();
        };
        return true;
      }
      if (pref === "unset" && !seenPromptRef.current) {
        seenPromptRef.current = true;
        promptSourceRef.current = "background";
        setPermPrompt(true);
      }
      return false;
    },
    [openConversation, dismissForConversation]
  );

  /**
   * Called when opening fullscreen Messages. Auto-enables if browser already
   * granted permission; otherwise soft-asks once per session while pref is unset.
   * Does not call Notification.requestPermission() (needs a user gesture).
   */
  const suggestDesktopNotifications = useCallback(() => {
    if (typeof Notification === "undefined") return;
    const pref = getDesktopNotifPref();
    if (pref !== "unset") return;

    if (Notification.permission === "granted") {
      setDesktopNotifPref("granted");
      setDesktopEnabledState(true);
      return;
    }
    if (Notification.permission === "denied") return;
    if (wasMessagesTipDismissed()) return;
    if (permPrompt) return;

    promptSourceRef.current = "messages";
    setPermPrompt(true);
  }, [permPrompt]);

  useEffect(() => {
    return subscribe((event, payload) => {
      if (event !== "dm.message" && event !== "dm.message.reaction") return;
      const orgId = payload?.org_id || socketOrgId || selectedOrgId;
      if (!orgId || orgId === "social") return;
      if (myPresence === "dnd") return;

      const conversationId = payload?.conversation_id;
      if (!conversationId) return;
      if (isConversationMuted(conversationId)) return;

      // Suppress alerts when this tab (or another Circa tab) is viewing the thread.
      const pageVisible = typeof document !== "undefined" && !document.hidden;
      const drawerShowingThread =
        open &&
        !minimized &&
        Number(activeConversationId) === Number(conversationId);
      const viewingHere =
        pageVisible &&
        (isDmViewingHere(orgId, conversationId) ||
          drawerShowingThread ||
          isViewingConversation(location.pathname, orgId, conversationId));
      const viewingElsewhere = isDmViewedElsewhere(orgId, conversationId);

      let title;
      let body;
      let messageId;

      if (event === "dm.message.reaction") {
        if (!payload?.added) return;
        if (payload.user_id === currentUserId) return;
        // Only alert the author of the reacted message
        if (
          payload.message_author_id &&
          payload.message_author_id !== currentUserId
        ) {
          return;
        }
        const reactorName = payload.reactor_name || "Someone";
        const emoji = payload.emoji || "👍";
        title = reactorName;
        body =
          payload.preview ||
          `${reactorName} reacted ${emoji} to your message`;
        messageId = payload.message_id;
      } else {
        const msg = payload?.message;
        if (!msg) return;
        if (msg.sender_id === currentUserId || msg.sender_id === "system")
          return;
        title = msg.sender
          ? [msg.sender.first_name, msg.sender.last_name]
              .filter(Boolean)
              .join(" ")
          : "Someone";
        body =
          plainExcerpt(msg.content, 100) ||
          (msg.attachment ? "Shared a post" : "New message");
        messageId = msg.message_id;
      }

      // Open here: sound only. Open in another tab: silence this tab entirely.
      if (!viewingHere && !viewingElsewhere) {
        const showedDesktop = maybeDesktopNotify({
          title:
            event === "dm.message.reaction"
              ? body
              : `Message from ${title}`,
          body,
          conversationId,
          orgId: String(orgId),
        });

        if (!showedDesktop) {
          pushToast({
            orgId: String(orgId),
            conversationId,
            messageId,
            title,
            body,
          });
        }
      }

      if (viewingElsewhere && !viewingHere) return;

      if (myPresence !== "busy") {
        playDmSound();
      }
    });
  }, [
    subscribe,
    socketOrgId,
    selectedOrgId,
    currentUserId,
    myPresence,
    open,
    minimized,
    activeConversationId,
    location.pathname,
    pushToast,
    maybeDesktopNotify,
  ]);

  // Opening a thread (drawer or full page) clears that conversation's toasts
  useEffect(() => {
    if (open && activeConversationId) {
      dismissForConversation(activeConversationId);
    }
  }, [open, activeConversationId, dismissForConversation]);

  useEffect(() => {
    if (isViewingConversation(location.pathname, selectedOrgId, activeConversationId)) {
      dismissForConversation(activeConversationId);
    }
    const match = location.pathname.match(/^\/o\/[^/]+\/messages\/(\d+)/);
    if (match) {
      dismissForConversation(Number(match[1]));
    }
  }, [location.pathname, selectedOrgId, activeConversationId, dismissForConversation]);

  const replyFromToast = useCallback(
    async (toast, content) => {
      if (!accessToken || !toast?.orgId || !toast?.conversationId) return;
      const text = (content || "").trim();
      if (!text) return;
      await sendDmMessage(accessToken, toast.orgId, toast.conversationId, {
        content: text,
      });
      dismiss(toast.id);
    },
    [accessToken, dismiss]
  );

  const reactFromToast = useCallback(
    async (toast, emoji) => {
      if (
        !accessToken ||
        !toast?.orgId ||
        !toast?.conversationId ||
        !toast?.messageId
      ) {
        return;
      }
      await toggleDmReaction(
        accessToken,
        toast.orgId,
        toast.conversationId,
        toast.messageId,
        emoji
      );
      dismiss(toast.id);
    },
    [accessToken, dismiss]
  );

  const value = {
    requestDesktopPermission,
    setDesktopEnabled,
    suggestDesktopNotifications,
    desktopEnabled,
    desktopPref: getDesktopNotifPref(),
  };

  return (
    <DmAlertContext.Provider value={value}>
      {children}
      <div className="fixed bottom-20 right-4 z-50 flex flex-col gap-2 w-80 max-w-[calc(100vw-2rem)] pointer-events-none">
        {permPrompt && (
          <div className="pointer-events-auto rounded-lg border border-border bg-background shadow-lg p-3 text-sm space-y-2">
            <p className="font-medium">Desktop notifications</p>
            <p className="text-xs text-muted-foreground">
              Get alerted when you receive a message while this tab is in the
              background.
            </p>
            <div className="flex gap-2 justify-end">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-7 text-xs"
                onClick={() => {
                  // Session dismiss only — do not set pref denied (sidebar Off does that).
                  if (promptSourceRef.current === "messages") {
                    markMessagesTipDismissed();
                  }
                  setPermPrompt(false);
                }}
              >
                Not now
              </Button>
              <Button
                type="button"
                size="sm"
                className="h-7 text-xs"
                onClick={async () => {
                  await requestDesktopPermission();
                  setPermPrompt(false);
                }}
              >
                Enable
              </Button>
            </div>
          </div>
        )}
        {toasts.map((t) => (
          <DmToastCard
            key={t.id}
            toast={t}
            onDismiss={() => dismiss(t.id)}
            onHold={() => holdToast(t.id)}
            onRelease={() => releaseToast(t.id)}
            onOpen={() => {
              openConversation?.(t.conversationId);
              dismiss(t.id);
            }}
            onReply={replyFromToast}
            onReact={reactFromToast}
          />
        ))}
      </div>
    </DmAlertContext.Provider>
  );
}

function DmToastCard({
  toast,
  onDismiss,
  onHold,
  onRelease,
  onOpen,
  onReply,
  onReact,
}) {
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);
  const [showReply, setShowReply] = useState(false);
  const [reacting, setReacting] = useState(false);
  const holdingRef = useRef(false);

  const hold = () => {
    if (holdingRef.current) return;
    holdingRef.current = true;
    onHold?.();
  };

  const release = () => {
    if (!holdingRef.current) return;
    holdingRef.current = false;
    onRelease?.();
  };

  useEffect(() => {
    if (showReply) hold();
    else release();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hold while reply composer is open
  }, [showReply]);

  useEffect(
    () => () => {
      holdingRef.current = false;
    },
    []
  );

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!reply.trim() || sending) return;
    setSending(true);
    try {
      await onReply(toast, reply);
    } catch (err) {
      console.error("Toast reply failed:", err);
    } finally {
      setSending(false);
    }
  };

  const quickReact = async (emoji) => {
    if (reacting || !toast.messageId) return;
    setReacting(true);
    try {
      await onReact(toast, emoji);
    } catch (err) {
      console.error("Toast react failed:", err);
    } finally {
      setReacting(false);
    }
  };

  return (
    <div className="pointer-events-auto rounded-lg border border-border bg-background shadow-lg p-3 text-sm">
      <div className="flex items-start gap-2">
        <button
          type="button"
          className="flex-1 min-w-0 text-left"
          onClick={onOpen}
        >
          <p className="font-medium truncate">{toast.title}</p>
          <p className="text-xs text-muted-foreground line-clamp-2 mt-0.5">
            {toast.body}
          </p>
        </button>
        <button
          type="button"
          className="p-0.5 text-muted-foreground hover:text-foreground"
          onClick={onDismiss}
          aria-label="Dismiss"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
      {toast.messageId && (
        <div className="mt-2 flex items-center gap-0.5">
          {DM_QUICK_REACT_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              disabled={reacting}
              className="h-7 w-7 rounded-md text-sm hover:bg-muted disabled:opacity-50"
              title={`React ${emoji}`}
              onClick={() => quickReact(emoji)}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}
      {showReply ? (
        <form onSubmit={submit} className="mt-2 flex gap-1">
          <input
            value={reply}
            onChange={(e) => setReply(e.target.value)}
            onFocus={hold}
            placeholder="Reply…"
            className="flex-1 min-w-0 rounded-md border border-input bg-background px-2 py-1 text-xs"
            disabled={sending}
            autoFocus
          />
          <Button
            type="submit"
            size="sm"
            className="h-7 text-xs px-2"
            disabled={sending || !reply.trim()}
          >
            Send
          </Button>
        </form>
      ) : (
        <div className="mt-2 flex gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-7 text-xs"
            onClick={() => setShowReply(true)}
          >
            Reply
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            onClick={onOpen}
          >
            Open
          </Button>
        </div>
      )}
    </div>
  );
}

export function useDmAlerts() {
  const ctx = useContext(DmAlertContext);
  if (!ctx) {
    throw new Error("useDmAlerts must be used within DmAlertProvider");
  }
  return ctx;
}
